import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Button, BackButton, LoadingButton, PillButton, Dropdown, useToast } from '../../components/ui';

const examplePrompts = [
  { id: 1, title: 'AI Tool Recommendations', description: 'Share the latest AI tools and insights' },
  { id: 2, title: 'Marketing Strategy Analysis', description: 'Analyze successful social media marketing cases' },
  { id: 3, title: 'Design Inspiration', description: 'Share design trends and creative ideas' },
  { id: 4, title: 'Product Reviews', description: 'In-depth reviews of popular products and services' },
  { id: 5, title: 'Industry Trends', description: 'Analyze the latest industry dynamics and trends' },
  { id: 6, title: 'Tech Tutorials', description: 'Share practical technical tutorials and tips' }
];

function ContentGeneratorIdea() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // 內容設定
  const [pageCount, setPageCount] = useState('5');
  const [outputLanguage, setOutputLanguage] = useState('zh-TW');

  const handleContinue = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Please enter your idea or topic');
      return;
    }

    setIsCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/projects/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'content-generator',
          source: 'idea',
          currentStep: 2,
          keywords: [searchQuery],
          topic: searchQuery,
          pageCount: parseInt(pageCount),
          contentType: 'carousel',
          language: outputLanguage,
          preset: 'default',
          generationStatus: 'draft'
        })
      });

      const result = await response.json();
      if (result.success) {
        navigate(`/templates/content-generator/step2?projectId=${result.project.id}`);
      } else {
        toast.error('Failed to create project');
      }
    } catch (error) {
      console.error('Failed to create project:', error);
      toast.error('Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Home Button */}
      <Button
        onClick={() => navigate('/home')}
        variant="primary"
        size="md"
        icon="mdi:home"
        className="fixed top-8 left-8 z-[100]"
      >
        Home
      </Button>

      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/templates/content-generator')}
        className="fixed bottom-8 left-8 z-[100]"
      >
        Back
      </BackButton>

      {/* Main Content */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 rounded-2xl flex items-center justify-center">
            <Icon icon="mdi:lightbulb-outline" className="text-4xl text-amber-500" />
          </div>
          <h1 className="text-4xl font-bold text-black mb-2">Idea to Post</h1>
          <p className="text-base text-gray-500">Transform your ideas into engaging content</p>
        </div>

        {/* Input Section */}
        <div className="max-w-[600px] mx-auto">
          <div className="text-sm font-semibold text-slate-800 mb-4">
            What do you want to create?
          </div>
          <Input.TextArea
            size="large"
            placeholder="Enter your idea, topic, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            rows={4}
            className="rounded-lg mb-6 text-base"
          />

          <div className="text-xs text-slate-500 mb-3">Quick ideas:</div>
          <div className="flex flex-wrap gap-2 mb-6">
            {examplePrompts.slice(0, 4).map((example) => (
              <PillButton
                key={example.id}
                onClick={() => setSearchQuery(example.title)}
                selected={searchQuery === example.title}
              >
                {example.title}
              </PillButton>
            ))}
          </div>

          {/* Content Settings */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <div className="text-sm font-semibold text-slate-800 mb-4">內容設定</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Dropdown
                  label="輪播頁數"
                  value={pageCount}
                  onChange={(value) => setPageCount(value)}
                  size="md"
                  fullWidth
                  options={[
                    { value: '3', label: '3 頁' },
                    { value: '5', label: '5 頁' },
                    { value: '10', label: '10 頁' }
                  ]}
                />
              </div>
              <div>
                <Dropdown
                  label="輸出語言"
                  value={outputLanguage}
                  onChange={(value) => setOutputLanguage(value)}
                  size="md"
                  fullWidth
                  options={[
                    { value: 'zh-TW', label: '繁體中文' },
                    { value: 'en-US', label: 'English' },
                    { value: 'ja-JP', label: '日本語' }
                  ]}
                />
              </div>
            </div>
          </div>

          <LoadingButton
            onClick={handleContinue}
            disabled={!searchQuery.trim()}
            loading={isCreating}
            loadingText="Creating..."
            icon="mdi:arrow-right"
            variant="primary"
            size="lg"
            className="w-full"
          >
            Continue to Design
          </LoadingButton>
        </div>
      </div>
    </div>
  );
}

export default ContentGeneratorIdea;
